import { useState, useMemo } from 'react';
import { BudgetNodeIcon } from '@/components/budget/BudgetNodeIcon';
import { cn, formatCurrency } from '@/lib/utils';
import type { Budget } from '@/types';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TreeNode extends Omit<Budget, 'children'> {
  children: TreeNode[];
}

interface BudgetTreePickerProps {
  /** Flat list of all selectable budgets */
  budgets: Budget[];
  /** Currently selected parentId; '' means "no parent" */
  value: string;
  onChange: (id: string) => void;
  /** The budget being edited — itself and its entire subtree are hidden */
  excludeId?: string;
  label?: string;
  helperText?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildTree(
  budgets: Budget[],
  parentId: string | null,
  excludeId: string | undefined
): TreeNode[] {
  return budgets
    .filter((b) => b.id !== excludeId && (b.parentId ?? null) === parentId)
    .map((b) => ({
      ...b,
      children: buildTree(budgets, b.id, excludeId),
    }));
}

/** Returns the set of IDs that are ancestors of `selectedId` in the flat list */
function ancestorIds(budgets: Budget[], selectedId: string): Set<string> {
  const map = new Map(budgets.map((b) => [b.id, b]));
  const ids = new Set<string>();
  let cur = map.get(selectedId);
  while (cur?.parentId) {
    ids.add(cur.parentId);
    cur = map.get(cur.parentId);
  }
  return ids;
}

// ─── Single tree row ──────────────────────────────────────────────────────────

interface NodeRowProps {
  node: TreeNode;
  depth: number;
  selected: boolean;
  open: boolean;
  onToggle: () => void;
  onSelect: () => void;
}

function NodeRow({ node, depth, selected, open, onToggle, onSelect }: NodeRowProps) {
  const hasChildren = node.children.length > 0;
  const indent = depth * 16; // px per level

  return (
    <div
      role="option"
      aria-selected={selected}
      className={cn(
        'flex items-center gap-1.5 py-1.5 pr-3 text-sm rounded-md cursor-pointer select-none',
        'hover:bg-muted/60 transition-colors',
        selected && 'bg-primary/10 text-primary font-medium'
      )}
      style={{ paddingLeft: `${8 + indent}px` }}
      onClick={onSelect}
    >
      {/* Expand / collapse toggle */}
      <span
        className={cn(
          'flex-shrink-0 w-4 h-4 flex items-center justify-center rounded transition-transform',
          !hasChildren && 'invisible'
        )}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        role="button"
        aria-label={open ? 'Collapse' : 'Expand'}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn('transition-transform duration-150', open ? 'rotate-90' : 'rotate-0')}
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      </span>

      <BudgetNodeIcon hasChildren={hasChildren} />

      <span className="truncate flex-1 min-w-0">{node.name}</span>

      {/* Amount / remaining */}
      <span className="flex-shrink-0 text-right text-xs ml-2 space-y-0 leading-tight">
        <span className={cn(
          'block font-medium',
          selected ? 'text-primary' : 'text-foreground'
        )}>
          {formatCurrency(node.amount, node.currency)}
        </span>
        {(() => {
          const remaining = node.remaining ?? node.unallocated ?? (node.amount - (node.spent ?? 0));
          const isOver = remaining < 0;
          return (
            <span className={cn(
              'block',
              isOver ? 'text-destructive' : 'text-muted-foreground'
            )}>
              {isOver ? '-' : ''}{formatCurrency(Math.abs(remaining), node.currency)} left
            </span>
          );
        })()}
      </span>

      {selected && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="flex-shrink-0 ml-1"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
      )}
    </div>
  );
}

// ─── Recursive subtree renderer ────────────────────────────────────────────────

interface SubtreeProps {
  nodes: TreeNode[];
  depth: number;
  selectedId: string;
  openIds: Set<string>;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
}

function Subtree({ nodes, depth, selectedId, openIds, onToggle, onSelect }: SubtreeProps) {
  return (
    <>
      {nodes.map((node) => (
        <div key={node.id}>
          <NodeRow
            node={node}
            depth={depth}
            selected={selectedId === node.id}
            open={openIds.has(node.id)}
            onToggle={() => onToggle(node.id)}
            onSelect={() => onSelect(node.id)}
          />
          {openIds.has(node.id) && node.children.length > 0 && (
            <Subtree
              nodes={node.children}
              depth={depth + 1}
              selectedId={selectedId}
              openIds={openIds}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          )}
        </div>
      ))}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function BudgetTreePicker({
  budgets,
  value,
  onChange,
  excludeId,
  label,
  helperText,
}: BudgetTreePickerProps) {
  const tree = useMemo(
    () => buildTree(budgets, null, excludeId),
    [budgets, excludeId]
  );

  // Auto-open ancestors of the selected node so the selection is always visible
  const initialOpen = useMemo(() => {
    if (!value) return new Set<string>();
    return ancestorIds(budgets, value);
  }, [budgets, value]);

  const [openIds, setOpenIds] = useState<Set<string>>(initialOpen);

  const toggle = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelect = (id: string) => {
    // Clicking the already-selected item clears the selection
    onChange(id === value ? '' : id);
  };

  if (tree.length === 0) return null;

  return (
    <div className="space-y-1.5">
      {label && (
        <label className="block text-sm font-medium text-foreground">{label}</label>
      )}

      <div
        role="listbox"
        aria-label={label}
        className="border border-input rounded-lg bg-background overflow-y-auto max-h-64 p-1 space-y-0.5"
      >
        {/* "No parent" option */}
        <div
          role="option"
          aria-selected={value === ''}
          className={cn(
            'flex items-center gap-1.5 px-2 py-1.5 text-sm rounded-md cursor-pointer select-none',
            'hover:bg-muted/60 transition-colors',
            value === '' && 'bg-primary/10 text-primary font-medium'
          )}
          onClick={() => onChange('')}
        >
          {/* spacer matching chevron width */}
          <span className="w-4 h-4 flex-shrink-0" />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="flex-shrink-0 text-muted-foreground"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <span className="text-muted-foreground italic">No parent (top-level budget)</span>
          {value === '' && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="flex-shrink-0 ml-auto"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          )}
        </div>

        <Subtree
          nodes={tree}
          depth={0}
          selectedId={value}
          openIds={openIds}
          onToggle={toggle}
          onSelect={handleSelect}
        />
      </div>

      {helperText && (
        <p className="text-sm text-muted-foreground">{helperText}</p>
      )}
    </div>
  );
}
